import React from 'react'
import ReactECharts from 'echarts-for-react'
import i18n from 'i18next'
import Badge from '../../../UniformComponents/Badge'
import Typography from '../../../UniformComponents/Typography'

const GAUGE_DEGREES = 180
const NEEDLE_PATH = 'path://M0,2.9 A2.9,2.9 0 0,1 5.8,2.9 L5.8,117.1 A2.9,2.9 0 0,1 0,117.1 Z'
const BADGE_TO_TEXT_COLOR = {
    success: 'primary',
    warning: 'warning',
    error: 'error',
}

// endAngleInPct drives the gauge arc
// needleMin/MaxScore places needle
const getBands = (chartSubType) => ({
    rebook: [
        {
            endAngleInPct: 20, // starts at 180, ends at 144 (180 degrees - 20%)
            color: '#E85D5D',
            needleMinScore: 1.00, // score needs to be within [1, 1.49] for needle to land here
            needleMaxScore: 1.49,
            title: i18n.t('dashboard.rebook.band.1.title'),
            description: i18n.t('dashboard.rebook.band.1.description'),
            badgeTitle: i18n.t('dashboard.rebook.band.1.badgeTitle'),
            badgeType: 'error',
        },
        {
            endAngleInPct: 40, // from 144 to 108, so on...
            color: '#F0944A',
            needleMinScore: 1.50,
            needleMaxScore: 2.49,
            title: i18n.t('dashboard.rebook.band.2.title'),
            description: i18n.t('dashboard.rebook.band.2.description'),
            badgeTitle: i18n.t('dashboard.rebook.band.2.badgeTitle'),
            badgeType: 'error',
        },
        {
            endAngleInPct: 60,
            color: '#F5C842',
            needleMinScore: 2.50,
            needleMaxScore: 3.49,
            title: i18n.t('dashboard.rebook.band.3.title'),
            description: i18n.t('dashboard.rebook.band.3.description'),
            badgeTitle: i18n.t('dashboard.rebook.band.3.badgeTitle'),
            badgeType: 'warning',
        },
        {
            endAngleInPct: 80,
            color: '#A8C940',
            needleMinScore: 3.50,
            needleMaxScore: 4.49,
            title: i18n.t('dashboard.rebook.band.4.title'),
            description: i18n.t('dashboard.rebook.band.4.description'),
            badgeTitle: i18n.t('dashboard.rebook.band.4.badgeTitle'),
            badgeType: 'success',
        },
        {
            endAngleInPct: 100,
            color: '#4CAF50',
            needleMinScore: 4.50,
            needleMaxScore: 5.00,
            title: i18n.t('dashboard.rebook.band.5.title'),
            description: i18n.t('dashboard.rebook.band.5.description'),
            badgeTitle: i18n.t('dashboard.rebook.band.5.badgeTitle'),
            badgeType: 'success',
        },
    ],
    npsNew: [
        {
            endAngleInPct: 50,
            color: '#E85D5D',
            needleMinScore: -100,
            needleMaxScore: -1,
            title: i18n.t('dashboard.nps.band.title.event'),
            description: i18n.t('dashboard.nps.band.1.description'),
            badgeTitle: i18n.t('dashboard.nps.band.1.badgeTitle'),
            badgeType: 'error',
        },
        {
            endAngleInPct: 67,
            color: '#F5C842',
            needleMinScore: 0,
            needleMaxScore: 29,
            title: i18n.t('dashboard.nps.band.title.event'),
            description: i18n.t('dashboard.nps.band.2.description'),
            badgeTitle: i18n.t('dashboard.nps.band.2.badgeTitle'),
            badgeType: 'warning',
        },
        {
            endAngleInPct: 84,
            color: '#A8C940',
            needleMinScore: 30,
            needleMaxScore: 49,
            title: i18n.t('dashboard.nps.band.title.event'),
            description: i18n.t('dashboard.nps.band.3.description'),
            badgeTitle: i18n.t('dashboard.nps.band.3.badgeTitle'),
            badgeType: 'success',
        },
        {
            endAngleInPct: 100,
            color: '#4CAF50',
            needleMinScore: 50,
            needleMaxScore: 100,
            title: i18n.t('dashboard.nps.band.title.event'),
            description: i18n.t('dashboard.nps.band.4.description'),
            badgeTitle: i18n.t('dashboard.nps.band.4.badgeTitle'),
            badgeType: 'success',
        },
    ],
    npsNewAvg: [
        {
            endAngleInPct: 65,
            color: '#E85D5D',
            needleMinScore: 0,
            needleMaxScore: 6.49,
            title: i18n.t('dashboard.nps.band.title.exhibitor'),
            description: i18n.t('dashboard.nps.avg.band.1.description'),
            badgeTitle: i18n.t('dashboard.nps.avg.band.1.badgeTitle'),
            badgeType: 'error',
        },
        {
            endAngleInPct: 85,
            color: '#F5C842',
            needleMinScore: 6.50,
            needleMaxScore: 8.49,
            title: i18n.t('dashboard.nps.band.title.exhibitor'),
            description: i18n.t('dashboard.nps.avg.band.2.description'),
            badgeTitle: i18n.t('dashboard.nps.avg.band.2.badgeTitle'),
            badgeType: 'warning',
        },
        {
            endAngleInPct: 100,
            color: '#4CAF50',
            needleMinScore: 8.50,
            needleMaxScore: 10.00,
            title: i18n.t('dashboard.nps.band.title.exhibitor'),
            description: i18n.t('dashboard.nps.avg.band.3.description'),
            badgeTitle: i18n.t('dashboard.nps.avg.band.3.badgeTitle'),
            badgeType: 'success',
        },
    ],
})[chartSubType]

const baseSeriesConfig = {
    type: 'gauge',
    radius: '90%',
    center: ['50%', '75%'],
    min: 0,
    max: 100,
    splitNumber: 0,
    axisLine: { lineStyle: { color: [[1, 'transparent']], width: 24 } },
    pointer: { show: false },
    progress: { show: false },
    axisTick: { show: false },
    splitLine: { show: false },
    axisLabel: { show: false },
    detail: { show: false },
    data: [{ value: 100 }],
}

const buildGaugeOption = (value, bands) => {
    // these series are only to mark range colors in gauge arc: `progress` vars
    const bandSeries = bands.map((band, i) => {
        const prevRangeEnd = i === 0 ? 0 : bands[i - 1].endAngleInPct
        const startAngle = 180 - (prevRangeEnd / 100) * GAUGE_DEGREES
        const endAngle = 180 - (band.endAngleInPct / 100) * GAUGE_DEGREES

        return {
            ...baseSeriesConfig,
            name: band.badgeTitle,
            startAngle,
            endAngle,
            progress: {
                show: true, roundCap: true, width: 32, itemStyle: { color: band.color },
            },
        }
    })

    // this series is what drives the needle
    const dataSeries = {
        ...baseSeriesConfig,
        startAngle: 180,
        endAngle: 0,
        z: 10,
        animationDuration: 1000,
        tooltip: { show: false },
        progress: { show: false },
        pointer: {
            icon: NEEDLE_PATH,
            length: '80%',
            width: 4,
            offsetCenter: [0, -6],
            itemStyle: { color: '#000' },
        },
        anchor: {
            show: true,
            showAbove: true,
            size: 14,
            itemStyle: { borderWidth: 4, borderColor: '#000' },
        },
        data: [{ value }],
    }

    return {
        tooltip: {
            trigger: 'item',
            formatter: (params) => (
                `<div style="text-align:center"><b>${params.seriesName}</b></div>`
            ),
        },
        animationDuration: 600,
        series: [...bandSeries, dataSeries],
    }
}

const normalizeScore = (rawScore, bands) => {
    const score = Math.max(bands[0].needleMinScore, Math.min(bands[bands.length - 1].needleMaxScore, rawScore))
    const idx = bands.findIndex((b) => score <= b.needleMaxScore)
    const band = bands[idx]
    const gaugeMin = idx === 0 ? 0 : bands[idx - 1].endAngleInPct
    const relPos = (score - band.needleMinScore) / (band.needleMaxScore - band.needleMinScore)
    return gaugeMin + relPos * (band.endAngleInPct - gaugeMin)
}

const getScoreInfo = (rawScore, chartSubType, isAvgScore) => {
    const chartSubSubType = chartSubType === 'npsNew' && isAvgScore ? 'npsNewAvg' : chartSubType
    const scoreBands = getBands(chartSubSubType)
    const maxScore = scoreBands[scoreBands.length - 1].needleMaxScore
    const clipped = Math.max(scoreBands[0].needleMinScore, Math.min(maxScore, rawScore))
    const normalizedScore = normalizeScore(rawScore, scoreBands)
    const scoreDetails = scoreBands.find((b) => clipped <= b.needleMaxScore)
    return { normalizedScore, scoreDetails, scoreBands }
}

const getNpsTitle = (rawScore, isExhibitor) => {
    const prefix = !isExhibitor && rawScore >= 0 ? '+' : ''
    let val = rawScore
    if (isExhibitor) val = (Number.isInteger(rawScore) ? rawScore : rawScore.toFixed(1))
    else val = Math.round(rawScore)

    const score = `${prefix}${val}`
    return i18n.t(`dashboard.nps.band.title.${isExhibitor ? 'exhibitor' : 'event'}`, { score })
}

const renderFooter = (chartSubType, scoreDetails, rawScore, numOfResponses, isAvgScore) => {
    const isNps = chartSubType === 'npsNew'
    const npsTitle = isNps ? getNpsTitle(rawScore, isAvgScore) : ''

    return (
        <>
            <Badge isRounded cssClass="widget-badge" type={scoreDetails.badgeType} icon="fa-solid fa-circle">
                {scoreDetails.badgeTitle}
            </Badge>
            <Typography type="h4" color={BADGE_TO_TEXT_COLOR[scoreDetails.badgeType]}>
                {isNps ? npsTitle : scoreDetails.title}
            </Typography>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', textAlign: 'center' }}>
                <Typography type="p" color="grey">{scoreDetails.description}</Typography>
                {isNps ? (
                    numOfResponses != null && (
                        <Typography type="p" color="grey">
                            {i18n.t('dashboard.nps.numOfResponses', { numOfResponses })}
                        </Typography>
                    )
                ) : (
                    <Typography type="p" color="grey">
                        {i18n.t('dashboard.rebook.lor')}
                        <strong style={{ color: '#000' }}>{` ${rawScore.toFixed(1)}/5 `}</strong>
                        {numOfResponses != null ? ` · ${numOfResponses} ${i18n.t('dashboard.rebook.responses')}` : ''}
                    </Typography>
                )}
            </div>
        </>
    )
}

const renderNoData = () => (
    <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        color: 'grey',
    }}
    >
        ⚠️ No data
    </div>
)

const GaugeNew = ({
    chartData, chartSubType, isAvgScore = false, isPreview,
}) => {
    const latestData = chartData?.slice().sort((a, b) => b.year - a.year)[0]
    const rawScore = latestData?.value ?? null

    if (rawScore === null) return renderNoData()

    const {
        normalizedScore,
        scoreDetails,
        scoreBands,
    } = getScoreInfo(rawScore, chartSubType, isAvgScore)

    const numOfResponses = latestData?.numOfResponses

    return (
        <>
            <ReactECharts
                option={buildGaugeOption(normalizedScore, scoreBands)}
                style={{ marginTop: isPreview ? '0px' : '-90px' }}
                notMerge // Replaces `option` on re-render - useful if changing subtypes (clear prev band config)
            />

            <div style={{
                marginTop: '-36px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
            }}
            >
                {renderFooter(chartSubType, scoreDetails, rawScore, numOfResponses, isAvgScore)}
            </div>
        </>
    )
}

export default GaugeNew
